// 4章 地業工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_JIGYO_MAP = `
<svg viewBox="0 0 660 270" role="img" aria-label="地業工事の全体像。杭地業には既製コンクリート杭、鋼杭、場所打ちコンクリート杭がある。地盤改良には深層混合処理と浅層混合処理がある。そのほか砂利、砂、捨てコンクリート地業がある">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="20"  y="60" width="200" height="180" rx="10"/>
    <rect x="235" y="60" width="200" height="180" rx="10"/>
    <rect x="450" y="60" width="190" height="180" rx="10"/>
  </g>
  <g fill="currentColor" font-size="13.5" font-weight="bold" text-anchor="middle">
    <text x="120" y="90">杭地業</text>
    <text x="335" y="90">地盤改良</text>
    <text x="545" y="90">直接系の地業</text>
  </g>
  <g fill="currentColor" font-size="12">
    <text x="36" y="120">・既製コンクリート杭（3節）</text>
    <text x="36" y="144">・鋼杭（4節）</text>
    <text x="36" y="168">・場所打ちコンクリート杭</text>
    <text x="48" y="188">（5節）</text>
    <text x="36" y="216" opacity="0.75">→ 硬い支持層まで力を届ける</text>
    <text x="251" y="120">・深層混合処理（7節）</text>
    <text x="263" y="140" opacity="0.85">柱状に地中で固める</text>
    <text x="251" y="166">・浅層混合処理（8節）</text>
    <text x="263" y="186" opacity="0.85">浅い範囲を面的に固める</text>
    <text x="251" y="216" opacity="0.75">→ 地盤そのものを強くする</text>
    <text x="466" y="120">・砂利地業（6節）</text>
    <text x="466" y="144">・砂地業（6節）</text>
    <text x="466" y="168">・捨コンクリート地業（6節）</text>
    <text x="466" y="216" opacity="0.75">→ 基礎下を整え支える</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="20" y="40">地業＝建物の荷重を地盤に伝えるための工事。試験（2節）で支持力・支持層を確認しながら進める</text>
  </g>
</svg>`;

const SVG_SHIKENGUI = `
<svg viewBox="0 0 660 230" role="img" aria-label="試験杭の流れ。本杭に先立って試験杭を施工し、支持層を確認して本杭の管理基準を定める。試験杭の施工設備は本杭と同じものを使う。全ての本杭は管理基準と照合しながら施工する">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="20"  y="50" width="180" height="80" rx="8"/>
    <rect x="240" y="50" width="180" height="80" rx="8"/>
    <rect x="460" y="50" width="180" height="80" rx="8"/>
  </g>
  <g stroke="currentColor" stroke-width="1.8">
    <line x1="200" y1="90" x2="232" y2="90"/>
    <line x1="420" y1="90" x2="452" y2="90"/>
  </g>
  <g fill="currentColor">
    <polygon points="240,90 228,84 228,96"/>
    <polygon points="460,90 448,84 448,96"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="110" y="82">① 試験杭</text>
    <text x="330" y="82">② 管理基準を定める</text>
    <text x="550" y="82">③ 本杭</text>
  </g>
  <g fill="currentColor" font-size="11.5" text-anchor="middle" opacity="0.85">
    <text x="110" y="106">本杭に先立ち施工</text>
    <text x="330" y="106">支持層の確認・電流値等</text>
    <text x="550" y="106">全数を基準と照合</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="20" y="170">・試験杭の施工設備は本杭と同じものを使う（条件を揃えるため）</text>
    <text x="20" y="192">・位置・本数・寸法は特記による。監督職員の立会いのもと試験を行う</text>
    <text x="20" y="214">・予定深さで支持層が出ない等の異常は、施工を止めて監督職員と協議（4.1.3）</text>
  </g>
</svg>`;

const SVG_PLATE_LOAD_TEST = `
<svg viewBox="0 0 660 300" role="img" aria-label="4.2.4 地盤の平板載荷試験。乱さず水平に整形した試験地盤に円形鋼製載荷板を密着させ、載荷板の中央に荷重計と油圧ジャッキを鉛直に設置し、重機を利用した反力装置に向けて段階的に載荷する。反力装置から独立した基準梁に4個の変位計を固定し、載荷板の沈下量を測定する">
  <g fill="none" stroke="currentColor" stroke-width="2">
    <line x1="55" y1="250" x2="605" y2="250"/>
    <ellipse cx="330" cy="236" rx="75" ry="12"/>
    <rect x="300" y="155" width="60" height="75" rx="6"/>
    <rect x="290" y="132" width="80" height="23" rx="4"/>
    <line x1="150" y1="105" x2="510" y2="105" stroke-width="12"/>
    <line x1="105" y1="175" x2="555" y2="175" stroke-width="5"/>
    <line x1="215" y1="175" x2="245" y2="225"/>
    <line x1="445" y1="175" x2="415" y2="225"/>
  </g>
  <g fill="currentColor" font-size="13" text-anchor="middle">
    <text x="330" y="45">反力装置</text>
    <text x="330" y="95">反力梁</text>
    <text x="330" y="127">荷重計</text>
    <text x="385" y="198">油圧ジャッキ</text>
    <text x="330" y="275">円形鋼製載荷板・乱さない試験地盤</text>
    <text x="150" y="165">独立した基準梁</text>
    <text x="205" y="215">変位計</text>
    <text x="455" y="215">変位計</text>
  </g>
</svg>`;

const SVG_CEMENT_MILK = `
<svg viewBox="0 0 660 300" role="img" aria-label="セメントミルク工法の手順。アースオーガーで杭径プラス100ミリ程度の孔を掘削し、支持層に達したら根固め液と杭周固定液を注入しながらオーガーを引き抜き、既製杭を建て込んで軽打またはヤットコで根固め液中に貫入させる">
  <g fill="currentColor" opacity="0.2">
    <rect x="40" y="220" width="580" height="30"/>
  </g>
  <g fill="none" stroke="currentColor" stroke-width="2">
    <rect x="80" y="60" width="44" height="160"/>
    <rect x="280" y="60" width="44" height="160"/>
    <rect x="480" y="60" width="44" height="160"/>
  </g>
  <g stroke="currentColor" stroke-width="5" stroke-linecap="round">
    <line x1="102" y1="40" x2="102" y2="200"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5" opacity="0.8">
    <path d="M92 70 h20 M92 100 h20 M92 130 h20 M92 160 h20"/>
  </g>
  <g fill="currentColor" opacity="0.45">
    <rect x="282" y="150" width="40" height="68"/>
    <rect x="282" y="62" width="40" height="88" opacity="0.5"/>
  </g>
  <g fill="currentColor" opacity="0.85">
    <rect x="492" y="50" width="20" height="150" rx="3"/>
  </g>
  <g fill="currentColor" opacity="0.45">
    <rect x="482" y="150" width="40" height="68"/>
  </g>
  <g fill="currentColor" font-size="12.5" font-weight="bold" text-anchor="middle">
    <text x="102" y="30">① 掘削</text>
    <text x="302" y="30">② 注入</text>
    <text x="502" y="30">③ 杭の建込み</text>
  </g>
  <g fill="currentColor" font-size="11.5" text-anchor="middle" opacity="0.85">
    <text x="102" y="246">アースオーガー</text>
    <text x="102" y="262">（径＝杭径＋100mm程度）</text>
    <text x="302" y="246">根固め液（下部）＋</text>
    <text x="302" y="262">杭周固定液（引抜きつつ注入）</text>
    <text x="502" y="246">軽打（2ｔ×落下高0.5ｍ程度）</text>
    <text x="502" y="262">で根固め液中に貫入</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="40" y="290">根固め液：W/C 70%以下・圧縮強度20N/mm²以上／杭周固定液：0.5N/mm²以上。オーガーは逆回転で引き抜かない</text>
  </g>
</svg>`;

const SVG_TREMIE = `
<svg viewBox="0 0 660 300" role="img" aria-label="場所打ちコンクリート杭のトレミー打設。掘削孔に鉄筋かごを設置し、トレミー管でコンクリートを打ち込む。トレミー管の先端は常にコンクリート中に2メートル以上入れて保持し、杭頭には余盛りを行う">
  <g fill="currentColor" opacity="0.15">
    <rect x="200" y="40" width="180" height="220"/>
  </g>
  <g stroke="currentColor" stroke-width="2" fill="none">
    <line x1="200" y1="40" x2="200" y2="260"/>
    <line x1="380" y1="40" x2="380" y2="260"/>
    <line x1="200" y1="260" x2="380" y2="260"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5" opacity="0.6">
    <line x1="215" y1="50" x2="215" y2="255"/>
    <line x1="365" y1="50" x2="365" y2="255"/>
    <path d="M215 80 h150 M215 130 h150 M215 180 h150 M215 230 h150"/>
  </g>
  <g fill="currentColor" opacity="0.5">
    <rect x="202" y="160" width="176" height="98"/>
  </g>
  <g fill="currentColor" opacity="0.9">
    <rect x="278" y="20" width="24" height="180" rx="3"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="420" y1="160" x2="420" y2="200"/>
    <line x1="412" y1="160" x2="428" y2="160"/>
    <line x1="412" y1="200" x2="428" y2="200"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="440" y="60" font-weight="bold">トレミー管</text>
    <text x="440" y="84" opacity="0.85">コンクリートを孔底から</text>
    <text x="440" y="102" opacity="0.85">押し上げるように打つ</text>
    <text x="440" y="184" font-weight="bold">先端は常に2ｍ以上</text>
    <text x="440" y="204" font-weight="bold">コンクリート中に保持</text>
    <text x="440" y="240" opacity="0.85">杭頭余盛り：A種500mm以上</text>
    <text x="440" y="258" opacity="0.85">　　　　　　B種800mm以上</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.85">
    <text x="60" y="60">鉄筋かご</text>
    <text x="60" y="80">（スペーサー付き）</text>
    <text x="60" y="240">スライムは処理して</text>
    <text x="60" y="258">から打込み</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="40" y="290">打込み開始時はプランジャー使用。泥水・土砂を巻き込まないための「2ｍルール」が品質のキモ（4.5.5）</text>
  </g>
</svg>`;

const SVG_JARI = `
<svg viewBox="0 0 640 240" role="img" aria-label="砂利地業。厚さは特記がなければ60ミリ。敷き均して締め固める。締固めはランマー3回突きや振動コンパクター2回締め程度。厚さが300ミリを超える場合は300ミリごとに締め固める">
  <g fill="currentColor" opacity="0.2"><rect x="60" y="170" width="520" height="40"/></g>
  <g fill="currentColor" opacity="0.55"><rect x="60" y="140" width="520" height="30"/></g>
  <g fill="currentColor" opacity="0.35"><rect x="60" y="120" width="520" height="20"/></g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="600" y1="140" x2="600" y2="170"/>
    <line x1="592" y1="140" x2="608" y2="140"/>
    <line x1="592" y1="170" x2="608" y2="170"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="60" y="60" font-weight="bold">砂利地業：厚さ60mm（特記がなければ）</text>
    <text x="60" y="88" opacity="0.85">材料＝再生クラッシャラン・切込砂利・切込砕石（C-40程度）</text>
    <text x="380" y="115" opacity="0.85">捨コンクリート（厚50mm・平たん）</text>
    <text x="70" y="162" opacity="0.9">砂利（敷き均して締固め）</text>
    <text x="70" y="198" opacity="0.9">根切り底（床付け面）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="60" y="232">締固め＝ランマー3回突き・振動コンパクター2回締め程度。厚300mm超は300mmごとに締固め（4.6.3）</text>
  </g>
</svg>`;

const SVG_BOSHITSU = `
<svg viewBox="0 0 660 240" role="img" aria-label="床下防湿層。ポリエチレンフィルム厚さ0.15ミリ以上を土間スラブ直下、断熱材がある場合は断熱材直下に敷く。重ね合わせと基礎梁際の折り下がりは250ミリ程度">
  <g fill="currentColor" opacity="0.5"><rect x="60" y="100" width="420" height="40"/></g>
  <g fill="currentColor" opacity="0.8"><rect x="480" y="60" width="50" height="150"/></g>
  <g stroke="currentColor" stroke-width="3" stroke-dasharray="10 5">
    <path d="M60 140 H470 Q480 140 480 150 V190"/>
  </g>
  <g fill="currentColor" opacity="0.2"><rect x="60" y="140" width="420" height="60"/></g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="500" y1="150" x2="500" y2="190"/>
    <line x1="492" y1="150" x2="508" y2="150"/>
    <line x1="492" y1="190" x2="508" y2="190"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="80" y="88" opacity="0.9">土間スラブ（コンクリート）</text>
    <text x="80" y="170" font-weight="bold">防湿層：ポリエチレンフィルム t0.15mm以上（スラブ直下）</text>
    <text x="520" y="174" font-weight="bold">折り下がり</text>
    <text x="520" y="192" font-weight="bold">250mm程度</text>
    <text x="536" y="120" opacity="0.85">基礎梁</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="60" y="228">重ね合わせも250mm程度。断熱材がある場合は「断熱材の直下」に敷く（4.6.5）</text>
  </g>
</svg>`;

const SVG_KAIRYO = `
<svg viewBox="0 0 660 280" role="img" aria-label="地盤改良の2方式。深層混合処理は地中に固化材を撹拌混合して柱状の改良体をつくる。浅層混合処理は基礎下の浅い範囲を面的に混合して固める">
  <g fill="currentColor" opacity="0.18">
    <rect x="30" y="90" width="280" height="150"/>
    <rect x="350" y="90" width="280" height="150"/>
  </g>
  <g fill="currentColor" opacity="0.55">
    <rect x="60" y="100" width="40" height="140" rx="6"/>
    <rect x="130" y="100" width="40" height="140" rx="6"/>
    <rect x="200" y="100" width="40" height="140" rx="6"/>
  </g>
  <g fill="currentColor" opacity="0.55">
    <rect x="370" y="100" width="240" height="50"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="170" y="50">深層混合処理（7節）</text>
    <text x="490" y="50">浅層混合処理（8節）</text>
  </g>
  <g fill="currentColor" font-size="11.5" text-anchor="middle" opacity="0.85">
    <text x="170" y="72">柱状の改良体を深くつくる</text>
    <text x="490" y="72">浅い範囲を面的に固める</text>
    <text x="170" y="262">機械撹拌方式が標準</text>
    <text x="490" y="262">原位置混合方式が標準</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="30" y="24">共通の流れ：試料土採取 → 室内配合試験（一軸圧縮＋六価クロム溶出）→ 施工 → 完了後試験（材齢28日）</text>
  </g>
</svg>`;

const ch04: Record<string, Commentary> = {
  "4.1.1": {
    plainSummary:
      "この章は地業工事（試験、既製コンクリート杭、鋼杭、場所打ちコンクリート杭、地盤改良、砂利・砂・捨コンクリート地業）のルール。1章とセットで適用する。",
    why: "地業は「建物の荷重を地盤へ伝える」工事です。①杭で硬い支持層まで届ける ②地盤改良で地盤自体を強くする ③砂利・捨コンで基礎下を整える、の3系統で章が構成されています。",
    diagrams: [
      {
        svg: SVG_JIGYO_MAP,
        caption: "地業工事の全体像（4章の構成）",
      },
    ],
  },

  "4.1.2": {
    plainSummary:
      "ゴールは3つ。①所定の材料 ②地業の位置・形状・寸法が上部構造物に有害な影響を与えない ③杭・地盤改良は所定の支持力を持つこと。",
  },

  "4.1.3": {
    plainSummary:
      "騒音・振動・排土・排水に配慮し、杭心・改良体心の位置を随時確認する。予定深さに届かない・支持層が出ない・所定の根入れが取れない・傾斜や孔壁崩落などの異常時は、監督職員と協議する。",
    why: "地中の工事は「予定どおりにいかないことが起こる」前提で組み立てられています。(6)の協議6項目は、地業工事のトラブル対応の型です。異常を隠して施工を続けるのが最悪の選択、とまず覚えてください。",
    points: [
      "協議6項目（届かない・支持層不明・根入れ不足・寸法不良・異常発生・性能不安）を頭に入れる",
      "杭心のずれは基礎配筋に直結。随時確認して記録",
    ],
  },

  "4.2.1": {
    plainSummary:
      "適切な時期に杭・支持地盤の位置と土質について試験を行い、支持力・支持地盤を確認する。試験は監督職員の立会いのもとで行い、その後の施工について協議する。",
  },

  "4.2.2": {
    plainSummary:
      "試験杭は本杭に先立ち施工し、その結果で本杭の管理基準（電流値・深さなど）を定める。施工設備は本杭と同じものを使う。位置・本数・寸法は特記による。",
    why: "地盤は場所ごとに違うため、図面だけでは本当の支持層は分かりません。試験杭は「この現場の答え合わせ」であり、ここで得た管理基準と全数照合しながら本杭を打つのが杭工事の基本構造です。",
    diagrams: [
      {
        svg: SVG_SHIKENGUI,
        caption: "試験杭→管理基準→本杭の流れ（4.2.2）",
      },
    ],
  },

  "4.2.3": {
    plainSummary:
      "杭の載荷試験（鉛直・水平）の適用・方法・位置・本数・荷重は特記による。",
    glossary: [
      {
        term: "載荷試験",
        meaning:
          "実際に荷重をかけて支持力や変形を直接確認する試験。鉛直（押込み）と水平がある。",
      },
    ],
  },

  "4.2.4": {
    plainSummary:
      "地盤の載荷試験は平板載荷試験とし、適用・方法は特記による。載荷板を置く地盤は乱さないようにする。",
    glossary: [
      {
        term: "平板載荷試験",
        meaning:
          "直径30cm程度の鋼板を地盤に置いて荷重をかけ、沈下量から地耐力を確認する試験。直接基礎の確認に使う。",
      },
    ],
    diagrams: [
      {
        svg: SVG_PLATE_LOAD_TEST,
        caption: "地盤の平板載荷試験の装置構成（4.2.4）",
      },
    ],
  },

  "4.2.5": {
    plainSummary:
      "施工完了後、工事概要・材料・工法・工程・写真・試験記録・施工記録をまとめた報告書を提出する。基礎ぐい工事の告示に基づく施工記録の保存期間も記載する。試験杭で採取した土砂は土質資料として提出。",
    why: "杭は完成すると絶対に見えません。だから「記録がすべて」。杭データ改ざん問題を受けて、施工記録の保存が告示で義務化された経緯も知っておきましょう。",
  },

  "4.3.1": {
    plainSummary:
      "この節はセメントミルク工法と特定埋込杭工法による既製コンクリート杭地業に適用する。",
    glossary: [
      {
        term: "既製コンクリート杭",
        meaning:
          "工場で製造された杭（PHC杭など）。現場では孔を掘って建て込む。品質が安定している。",
      },
    ],
  },

  "4.3.2": {
    plainSummary: "既製コンクリート杭地業には施工管理技術者を配置する。",
  },

  "4.3.3": {
    plainSummary:
      "杭は告示第1113号に基づく品質とし、種類・性能・寸法・継手箇所数・先端形状は特記による。溶接材料は7章、セメントは6章による。",
  },

  "4.3.4": {
    plainSummary:
      "セメントミルク工法＝アースオーガーで掘削した孔に根固め液・杭周固定液を注入して既製杭を建て込む工法。オーガー径は杭径＋100mm程度、引抜き時の逆回転禁止。杭は軽打（2ｔ×0.5ｍ程度）で根固め液中に貫入させ、7日程度養生。根固め液はW/C70％以下・圧縮強度20N/mm²以上、杭周固定液は0.5N/mm²以上。",
    why: "この工法の品質は「支持層への到達」と「根固め液」で決まります。支持層確認は掘削抵抗（電流値）の変化＋オーガーに付く土砂と土質調査資料の照合で行います。逆回転禁止は、オーガーに付いた土が孔に落ちて杭先端の支持力を損なうのを防ぐため。根固め液は杭先端と支持層を一体化させる「杭のかかと」です。",
    points: [
      "電流値の自動記録装置は必須設備",
      "根固め液の注入量＝掘削断面積×2ｍ以上",
      "管理試験：試験杭は1本ごと、本杭は30本ごと（継手ありは20本ごと）に1回・供試体3個",
    ],
    glossary: [
      {
        term: "根固め液",
        meaning:
          "杭先端部に注入する濃いセメントミルク。杭と支持層を固結させる。",
      },
      {
        term: "杭周固定液",
        meaning: "杭の周囲に注入する薄めのセメントミルク。杭周面の摩擦を確保する。",
      },
      {
        term: "ヤットコ",
        meaning:
          "杭頭を地盤面より深い位置に沈めるときに使う継ぎ道具。",
      },
    ],
    diagrams: [
      {
        svg: SVG_CEMENT_MILK,
        caption: "セメントミルク工法の手順（4.3.4）",
      },
    ],
  },

  "4.3.5": {
    plainSummary:
      "特定埋込杭工法＝告示第1113号第6に基づく認定工法（プレボーリング・中掘りなど各社工法）。根入れ長さ・精度・試験杭・本杭・記録は「工法ごとに定められた条件」による。",
    why: "市販の認定工法は、それぞれ大臣認定時の施工条件がセットになっています。仕様書は細部を決めず「工法ごとの条件を守れ」という構造です。使用工法の技術資料・認定条件を必ず入手して照合しましょう。",
  },

  "4.3.6": {
    plainSummary:
      "杭の継手は溶接継手（アーク溶接）または機械式継手。上下杭の軸線を一致させ、全ての溶接部を確認する。溶接後は急冷せず、時間をおいてから施工再開。",
    points: [
      "継手部の検査（外観・軸線）は建て込む前のこのタイミングしかできない",
    ],
  },

  "4.3.7": {
    plainSummary:
      "杭継手の溶接は、JIS Z 3801のA-2H程度またはWES 8106（基礎杭溶接技能者）等の資格を持つ技能資格者が行う。",
  },

  "4.3.8": {
    plainSummary:
      "杭頭の処理は特記による。杭の中空部にコンクリート等が落下しないよう、杭頭にふた等の措置を講ずる。",
  },

  "4.3.9": {
    plainSummary:
      "全ての杭について、掘削径・施工時間・支持層深さ・電流値・注入量・根入れ長さ・鉛直度・継手状態・位置ずれ・杭頭高さ等を記録する。",
    points: [
      "「全ての杭」の記録＝後日の証明。1本でも欠けると説明できなくなる",
    ],
  },

  "4.4.1": {
    plainSummary: "この節は特定埋込杭工法による鋼杭地業に適用する。",
    glossary: [
      {
        term: "鋼杭",
        meaning: "鋼管杭やH形鋼杭。軽くて運搬・継手がしやすく、変形能力が大きい。",
      },
    ],
  },

  "4.4.2": {
    plainSummary: "鋼杭地業には施工管理技術者を配置する。",
  },

  "4.4.3": {
    plainSummary: "鋼杭の材料は特記により、溶接材料は7章による。",
  },

  "4.4.4": {
    plainSummary: "工法は既製コンクリート杭の特定埋込杭工法（4.3.5）による。",
  },

  "4.4.5": {
    plainSummary:
      "鋼杭の継手は溶接（半自動または自動アーク溶接）か機械式。軸線を合わせ、全溶接部を確認し、急冷を避ける。",
  },

  "4.4.6": {
    plainSummary: "杭頭の処理は4.3.8による。",
  },

  "4.4.7": {
    plainSummary: "施工記録は工法ごとの条件によるほか4.3.9による。",
  },

  "4.5.1": {
    plainSummary:
      "この節はアースドリル工法・リバース工法・オールケーシング工法・場所打ち鋼管コンクリート杭工法と拡底杭工法に適用する。専門工事業者の施工能力を証明する資料を提出する。",
    glossary: [
      {
        term: "場所打ちコンクリート杭",
        meaning:
          "現場で孔を掘り、鉄筋かごを入れてコンクリートを打つ大径杭。アースドリル（安定液）・リバース（泥水循環）・オールケーシング（鋼管で孔壁保護）の3工法が代表。",
      },
      {
        term: "拡底杭",
        meaning: "杭先端を円すい状に広げて支持力を高めた場所打ち杭。",
      },
    ],
  },

  "4.5.2": {
    plainSummary: "場所打ちコンクリート杭地業には施工管理技術者を配置する。",
  },

  "4.5.3": {
    plainSummary:
      "鉄筋かご等の溶接は、7章の溶接技能資格者が行う。",
  },

  "4.5.4": {
    plainSummary:
      "鉄筋かごは主筋と帯筋を結束し（主筋への点付け溶接禁止）、継手は重ね継手（表5.3.2）、スペーサーで孔壁とのかぶりを確保。コンクリートはセメント＝高炉B種が標準、水中施工のB種は単位セメント量330kg/m³以上・W/C60％以下、スランプ21cm、構造体強度補正値S=3。単位水量最大200kg/m³。",
    why: "場所打ち杭のコンクリートは、泥水の中を2ｍ以上のかぶりで自然に流動して充填されます。だから普通のコンクリートより「セメント多め・軟らかめ」の特別仕様です。鉄筋かごのスペーサーが平鋼なのは、孔壁を削らないようにするためです。",
    points: [
      "スランプ試験は杭1本ごとに最初の運搬車でも実施",
      "強度判定は標準養生・材齢28日で調合管理強度以上",
    ],
    glossary: [
      {
        term: "鉄筋かご",
        meaning:
          "主筋と帯筋を円筒状に組んだ場所打ち杭の配筋。クレーンで吊って掘削孔に建て込む。",
      },
    ],
  },

  "4.5.5": {
    plainSummary:
      "試験杭で孔壁状況・安定液・スライム処理・支持層を確認し検査を受ける。本杭は掘削径＝設計径以上、スライム処理後直ちに鉄筋かご設置・コンクリート打込み。トレミー管の先端は常にコンクリート中に2ｍ以上。杭頭余盛りはA種500mm以上・B種800mm以上。近接杭は連続施工しない。",
    why: "場所打ち杭の3大敵は「孔壁崩壊・スライム・コンクリートの分離」。安定液で孔壁を守り、スライム（孔底に沈んだ泥）は処理してから打設し、トレミー管を2ｍ以上突っ込んでおくことで泥水と混ざらずに打ち上がります。余盛りは、泥と混ざった杭頭の劣化部分を後ではつり取るための「捨て代」です。",
    points: [
      "トレミー先端2ｍ・ケーシング先端2ｍの「2ｍルール」",
      "打込み開始時はプランジャーで管内の水と縁を切る",
      "隣の杭が固まらないうちに近接杭を掘らない",
    ],
    glossary: [
      {
        term: "スライム",
        meaning:
          "掘削中に孔底へ沈殿する泥・土砂。残ると杭先端の支持力が激減する。",
      },
      {
        term: "トレミー管",
        meaning:
          "水中コンクリート打設用の管。先端をコンクリート中に保ち、泥水との接触を防ぐ。",
      },
      {
        term: "安定液（ベントナイト液）",
        meaning: "孔壁に膜を作って崩壊を防ぐ泥水。比重・粘性・砂分を管理する。",
      },
    ],
    diagrams: [
      {
        svg: SVG_TREMIE,
        caption: "トレミー工法によるコンクリート打込み（4.5.5）",
      },
    ],
  },

  "4.5.6": {
    plainSummary:
      "場所打ち鋼管コンクリート杭・拡底杭は、工法ごとに定められた条件によるほか4.5.5による。",
  },

  "4.5.7": {
    plainSummary:
      "杭頭の余盛りは、打込みから14日程度経過後に、杭体を傷めないよう平らにはつり取り、所定の高さにそろえる。",
    why: "早くはつるとまだ強度の低い杭本体にひびを入れてしまいます。「14日程度待つ」「杭体を傷めない」の2点が杭頭処理の勘所。近年は静的破砕や杭頭処理工法も使われますが、原則は同じです。",
  },

  "4.5.8": {
    plainSummary:
      "全ての杭について、配筋・先端土質・孔壁状況・安定液・掘削深さ・支持層深さ・スライム処理・コンクリート投入量・施工時間・位置ずれ等を記録する。",
  },

  "4.6.1": {
    plainSummary: "この節は砂利・砂・捨コンクリート地業等に適用する。",
  },

  "4.6.2": {
    plainSummary:
      "砂利は再生クラッシャラン・切込砂利・切込砕石（C-40程度）。砂はシルト・有機物の混じらない山砂・川砂・砕砂。捨コンクリートは無筋コンクリート（6章14節）。床下防湿層はポリエチレンフィルムt0.15mm以上。",
    glossary: [
      {
        term: "クラッシャラン",
        meaning:
          "岩石を砕いてふるい分けた砕石。C-40は最大粒径40mmの粒度区分。",
      },
    ],
  },

  "4.6.3": {
    plainSummary:
      "砂利・砂地業の厚さは特記がなければ60mm。敷き均して締め固める（ランマー3回突き・振動コンパクター2回締め程度）。厚さ300mm超は300mmごとに締固め。防湿層を直接敷く場合は目つぶし砂を敷く。",
    why: "砂利地業は捨コンクリートと基礎の「座布団」です。締固め不足だと基礎が不均一に沈みます。目つぶし砂は、砂利の角でフィルムが破れるのを防ぐためのひと手間です。",
    diagrams: [
      {
        svg: SVG_JARI,
        caption: "砂利地業と捨コンクリートの構成（4.6.3〜4.6.4）",
      },
    ],
  },

  "4.6.4": {
    plainSummary:
      "捨コンクリートの厚さは特記がなければ50mmで平たんに仕上げる。他は無筋コンクリート（6章14節）による。",
    why: "捨コンクリートは構造体ではなく「墨出しのキャンバス＋鉄筋の作業床」です。平たんでないと基礎の墨も配筋精度も狂います。",
  },

  "4.6.5": {
    plainSummary:
      "床下防湿層の重ね合わせと基礎梁際の折り下がりは250mm程度。位置は土間スラブ直下（断熱材がある場合は断熱材の直下）。",
    diagrams: [
      {
        svg: SVG_BOSHITSU,
        caption: "床下防湿層の納まり（4.6.5）",
      },
    ],
  },

  "4.6.6": {
    plainSummary: "厚さ・締固め状況・仕上りレベルを確認して記録する。",
  },

  "4.7.1": {
    plainSummary:
      "この節は、地盤内に固化材を供給して対象土と強制的に撹拌混合する深層混合処理工法（柱状改良）に適用する。専門工事業者の能力証明資料を提出する。",
    diagrams: [
      {
        svg: SVG_KAIRYO,
        caption: "深層混合処理と浅層混合処理（7節・8節）",
      },
    ],
  },

  "4.7.2": {
    plainSummary: "固化材（セメント系など）の種類は特記による。",
  },

  "4.7.3": {
    plainSummary:
      "方式は特記がなければ機械撹拌方式。施工前に試料土で室内配合試験（一軸圧縮試験＋六価クロム溶出試験）を行い配合量を決める。支持層はオーガー電流値等の掘削抵抗で確認。完了後もコアの一軸圧縮試験（材齢28日）と六価クロム溶出試験を行う。",
    why: "改良体の強度は「土と固化材の相性」で決まるため、現場の土で事前に配合試験をします。セメント系固化材は土によって六価クロム（有害物質）が溶け出すことがあるため、溶出試験が事前・事後ともセットになっています。環境対応も工事の一部です。",
    glossary: [
      {
        term: "一軸圧縮試験",
        meaning: "円柱供試体を上下から圧縮して強さ（qu）を測る土の試験。",
      },
      {
        term: "六価クロム溶出試験",
        meaning:
          "改良土から有害な六価クロムが溶け出さないか確認する環境試験。",
      },
    ],
  },

  "4.7.4": {
    plainSummary:
      "全ての改良体について、心位置・径・天端レベル・固化材供給状況・撹拌混合状況・支持層深さ・電流値等を記録する。",
  },

  "4.8.1": {
    plainSummary:
      "この節は、基礎下端とその外側の必要領域を面的に混合処理する浅層混合処理工法に適用する。専門工事業者の能力証明資料を提出する。",
  },

  "4.8.2": {
    plainSummary: "固化材の種類は特記による。",
  },

  "4.8.3": {
    plainSummary:
      "方式は特記がなければ原位置混合方式。室内配合試験（一軸圧縮＋六価クロム溶出）で配合を決め、完了後もコア試験（材齢28日）を行う。改良強度・範囲・厚さは特記による。",
    points: [
      "バックホウ混合のムラが弱点になりやすい。混合回数・色ムラの確認を丁寧に",
      "転圧まで含めて浅層改良（記録項目にも「転圧状況」がある）",
    ],
  },

  "4.8.4": {
    plainSummary:
      "全ての改良体について、固化材量・改良範囲・改良厚さ・混合状況・転圧状況等を記録する。",
  },
};

export default ch04;
